import { Button, Checkbox, FileInput, Intent, Spinner } from "@blueprintjs/core"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import React, { useState } from "react"
import { useApi } from "../../../../hooks/useApi"
import { toaster } from "../../../../utils/createToaster"
import {
  ButtonContainer,
  DialogContent,
  HeaderText,
  MainContainer,
  OverwriteContainer,
  StyledDialog,
  SubHeaderText,
  TemplateButton,
  UploadButtonContent,
  WarningText,
} from "./PositionUploadDialog.styled"

interface PositionUploadDialogProps {
  isOpen: boolean
  onClose: () => void
}

export const PositionUploadDialog: React.FC<PositionUploadDialogProps> = ({ isOpen, onClose }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [fileInputKey, setFileInputKey] = useState(0) // Used to reset file input
  const [overwrite, setOverwrite] = useState(false)
  const queryClient = useQueryClient()
  const { scientApi } = useApi()

  const uploadMutation = useMutation({
    mutationFn: ({ file, overwrite }: { file: File; overwrite: boolean }) =>
      scientApi.trading.uploadPositionsCSV(file, overwrite || undefined),
    onSuccess: data => {
      toaster.show({
        message: `Upload successful! ${data.message}`,
        intent: Intent.SUCCESS,
      })
      setIsUploading(false)
      setSelectedFile(null)
      setOverwrite(false)
      setFileInputKey(prev => prev + 1) // Reset file input
      queryClient.invalidateQueries() // Invalidate all queries
      onClose()
    },
    onError: (error: any) => {
      toaster.show({
        message: error.response?.data?.error || "Upload failed. Please try again.",
        intent: Intent.DANGER,
      })
      setIsUploading(false)
    },
  })

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.name.endsWith(".csv")) {
        toaster.show({
          message: "Please select a CSV file",
          intent: Intent.DANGER,
        })
        setSelectedFile(null)
        return
      }

      // Validate file size (limit to 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toaster.show({
          message: "File size must be less than 10MB",
          intent: Intent.DANGER,
        })
        setSelectedFile(null)
        return
      }

      setSelectedFile(file)
    }
  }

  const handleUpload = () => {
    if (!selectedFile) return

    setIsUploading(true)
    uploadMutation.mutate({ file: selectedFile, overwrite })
  }

  const handleClose = () => {
    setSelectedFile(null)
    setIsUploading(false)
    setOverwrite(false)
    setFileInputKey(prev => prev + 1) // Reset file input
    onClose()
  }

  const downloadTemplate = () => {
    const csvContent =
      "account,ticker,quantity,book\nAccount1,AAPL US,100,BOOK1\nAccount1,MSFT US Equity,200,BOOK1"
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    const url = URL.createObjectURL(blob)
    link.setAttribute("href", url)
    link.setAttribute("download", "positions_template.csv")
    link.style.visibility = "hidden"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <StyledDialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Upload Positions CSV"
      className="bp5-dark"
    >
      <DialogContent>
        <MainContainer>
          <div>
            <HeaderText>Select a CSV file to upload positions</HeaderText>
            <SubHeaderText>
              Required columns: account, ticker, quantity. Optional: book, instrument_type
              <br />
              Available instrument_type values: ADR, Corporate Bond, Equity, Equity Option, Exchange
              Traded Fund, Index Future, Index Option, Index, Mutual Fund, Preferred, REIT, Warrant,
              Right, Govt/Treasury/Central Bank, Cash. If not provided, defaults to Equity.
            </SubHeaderText>
            <TemplateButton minimal intent={Intent.PRIMARY} onClick={downloadTemplate}>
              Download Template
            </TemplateButton>
          </div>

          <FileInput
            key={fileInputKey}
            text={selectedFile ? selectedFile.name : "Choose CSV file..."}
            onInputChange={handleFileChange}
            inputProps={{ accept: ".csv" }}
            fill
          />

          <OverwriteContainer>
            <Checkbox
              checked={overwrite}
              onChange={e => setOverwrite(e.target.checked)}
              disabled={!selectedFile}
              label="Overwrite Mode"
            />
            <WarningText>
              ⚠️ Warning: This will delete ALL existing positions for the accounts in your CSV file
              before uploading new data. This action cannot be undone.
            </WarningText>
          </OverwriteContainer>

          <ButtonContainer>
            <Button onClick={handleClose} disabled={isUploading}>
              Cancel
            </Button>
            <Button
              intent={Intent.PRIMARY}
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
            >
              {isUploading ? (
                <UploadButtonContent>
                  <Spinner size={16} />
                  Uploading...
                </UploadButtonContent>
              ) : (
                "Upload"
              )}
            </Button>
          </ButtonContainer>
        </MainContainer>
      </DialogContent>
    </StyledDialog>
  )
}

export default PositionUploadDialog
